import type { ClientContext } from '@/lib/clientContext';
import {
  entityDisplayName,
  entityLocation,
  formatAdviserName,
} from '@/lib/history';
import { IressCloseButton, IressTag } from '@iress-oss/ids-components';
import './ClientContextTag.css';

interface ClientContextTagProps {
  context: ClientContext;
  /** Called when the tag's × is activated (behaves like New chat). */
  onClear: () => void;
}

/**
 * The "Searching for" tag shown above the input when a client context is set.
 * Content: "<FirstName LastName> · <Suburb, State> · <FirstName LastName
 * adviser>", built entirely from the stored snapshot (no live fetch). Location
 * renders as "Parramatta, NSW" (either part may be absent) and the adviser name
 * is normalised to "First Last" order. Date of birth is intentionally NOT shown
 * here (it is still returned by the backend, used elsewhere). The × clears the
 * context (identical to New chat). The label clamps to two lines then
 * truncates; the × sits outside the clamped span so it always stays visible.
 */
export function ClientContextTag({ context, onClear }: ClientContextTagProps) {
  const parts = [entityDisplayName(context)];
  const location = entityLocation(context);
  if (location) parts.push(location);
  if (context.clientAdviserName) parts.push(formatAdviserName(context.clientAdviserName));
  const label = parts.join(' · ');

  return (
    <div className="porter-context">
      <span className="porter-context__prefix">Searching for</span>
      <IressTag
        mode="info"
        deleteButton={
          <IressCloseButton
            onClick={onClear}
            screenreaderText="Clear client context and start a new chat"
          />
        }
        className="porter-context__tag"
        title={label}
      >
        <span className="porter-context__label">{label}</span>
      </IressTag>
    </div>
  );
}
